var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import debug from 'debug';
import * as _ from 'lodash';
import { isFunction, get, throttle, delay, isUndefined, set, isObject, extend, isArray, filter, isString, isEmpty } from 'lodash';
import { Vue, Watch, Component, Prop } from 'vue-property-decorator';
import { uniqueID } from './unique-id';
import { createSearchDataSource } from './connections';
import { isComponentValueCache } from './caching';
const log = debug('itp:core:component-mixins');
const gettext = (value) => value;
function isPromise(value) {
    return value || isFunction(get(value, 'then'));
}
let HelpersMixin = class HelpersMixin extends Vue {
    _ = _.default;
    gettext(...args) {
        return this.$fn.gettext(...args);
    }
    getSchema(name) {
        return this.$app.connections.default.getSchema(name);
    }
    searchDataSource(operation, parameters) {
        return createSearchDataSource(this.$app.connections.default, operation, parameters);
    }
    randomElementId() {
        return uniqueID();
    }
    schemaDefaults(name, extras) {
        return this.$fn.schemaDefaults(name, extras);
    }
};
HelpersMixin = __decorate([
    Component
], HelpersMixin);
export { HelpersMixin };
let UserComponentMixin = class UserComponentMixin extends HelpersMixin {
    __usercomponent = true;
    $uid;
    $app;
    $imodal;
    routeTab;
    loadDataThrottled;
    canLoadData;
    dataMembers;
    subscriptions;
    loading;
    loaded;
    reloading;
    error;
    lastLoaded;
    document;
    componentMetadata;
    ux;
    forms;
    componentInitErrorCallback;
    componentInitReadyCallback;
    modal;
    modalName;
    reload;
    onReloadChange() {
        this.canLoadData && this.loadData();
    }
    get isModalComponent() {
        return !!this.modalName;
    }
    get friendlyName() {
        return this.modalName || this.$uid;
    }
    mixinData() {
        return {
            routeTab: null,
            canLoadData: true,
            loading: false,
            loaded: false,
            reloading: false,
            error: null,
            lastLoaded: null,
            forms: {}
        };
    }
    beforeCreate() { }
    created() {
        this.$uid = this.constructor.name;
        this.loadDataThrottled = throttle(this.loadData, 200);
        log('[%s] Component created %o', this.friendlyName, this);
        this.loadData(null, { initial: true })
            .then(() => {
            log('[%s] Component data loaded %o', this.friendlyName, this);
            this.$emit('componentLoaded', this);
            this.dataLoaded();
        })
            .catch(error => {
            if (this.componentInitErrorCallback) {
                this.componentInitErrorCallback(error);
            }
        });
    }
    dataLoaded() { }
    mounted() {
        log('[%s] Component mounted %o', this.friendlyName, this);
        this.updateHeaders();
    }
    activated() {
        log('[%s] Component activated %o', this.friendlyName, this);
        this.updateHeaders();
    }
    beforeDestroy() {
        if (this.isModalComponent || this.routeTab) {
            this.$app.documentTitle.reset();
        }
        this.unsubscribeFromGlobalEvents();
    }
    loadData(prop, opts) {
        if (!this.canLoadData) {
            log('[%s] loading is already in progress, prevent reload. property=%s', this.friendlyName, prop || 'all');
            return Promise.resolve();
        }
        if (!this.dataMembers || !this.dataMembers.length) {
            log('[%s] no data spec, nothing to load.', this.friendlyName);
            this.touchLastLoaded();
            return Promise.resolve();
        }
        let delay_ = 0;
        if (opts && opts.initial) {
            this.loading = true;
            if (this.ux && this.ux.initialLoadDelay) {
                delay_ = this.ux.initialLoadDelay;
            }
        }
        else {
            this.reloading = true;
        }
        return new Promise((resolve, reject) => {
            const handleError = (error) => {
                this.loading = false;
                this.reloading = false;
                this.handleError(error);
                reject(error);
            };
            try {
                log('[%s] loading data - %s', this.friendlyName, prop || 'all');
                delay(() => this._loadData({ useCache: (opts && opts.initial) || false })
                    .then(result => {
                    log('[%s] data: %o', this.friendlyName, result);
                    this.touchLastLoaded();
                    resolve();
                })
                    .catch(handleError), delay_);
            }
            catch (error) {
                handleError(error);
            }
        });
    }
    setData(path, value, options) {
        const currentValue = get(this, path);
        if (isUndefined(get(this, path))) {
            console.warn(`setData: ${path} path is undefined on component ${this.friendlyName}. ` +
                'This might be a problem if this value is expected to be reactive.');
        }
        set(this, path, value);
        log('%s := %o', path, value);
        if (!options || !options.noTouchLastLoaded) {
            this.touchLastLoaded();
        }
    }
    async _loadData(options) {
        const { useCache } = options || { useCache: false };
        const result = {};
        const setData = (name, value) => {
            this.$set(this, name, value);
            result[name] = value;
        };
        for (let name of this.dataMembers) {
            const uiSkipComponentReload = get(this, `${name}.uiSkipComponentReload`, false);
            if (uiSkipComponentReload) {
                continue;
            }
            if (useCache) {
                const cached = await this._loadFromCache(name);
                if (cached != null) {
                    const defaultValue = await this._loadDefaultData(name);
                    if (isObject(defaultValue) && isObject(cached)) {
                        extend({}, defaultValue, cached);
                        log('[%s] extend cache with default value: %o', defaultValue);
                    }
                    setData(name, cached);
                    continue;
                }
            }
            const defaultValue = await this._loadDefaultData(name);
            if (defaultValue != null) {
                setData(name, defaultValue);
            }
        }
        return result;
    }
    async _loadDefaultData(path) {
        const fn = get(this, `$$load_${path}`);
        if (isFunction(fn)) {
            return await fn();
        }
    }
    async _loadFromCache(path) {
        const handler = get(this, `$$cache_${path}`);
        if (isComponentValueCache(handler)) {
            const value = handler.getValue();
            if (value != null) {
                log('[%s] from cache: %s', this.friendlyName, path);
                return value;
            }
        }
    }
    updateRouteTab() {
        const title = this.documentTitle;
        log("updateRouteTab [%s] to '%s'", this.friendlyName, title);
        if (this.routeTab && title) {
            this.routeTab.title = title;
            this.routeTab.tips = title;
            this.routeTab.icon = this.document?.icon;
            return;
        }
        set(this, 'routeTab', { title, tips: title, icon: this.document?.icon });
    }
    subscribeToGlobalEvents() {
    }
    unsubscribeFromGlobalEvents() {
    }
    updateHeaders() {
        if (this.loaded && this.documentTitle) {
            this.$app.documentTitle.set(this.documentTitle);
            if (!this.isModalComponent) {
                this.updateRouteTab();
            }
        }
    }
    touchLastLoaded() {
        this.loading = false;
        this.reloading = false;
        this.loaded = true;
        this.error = null;
        this.lastLoaded = Math.round(Date.now() / 1000);
        log('[%s] lastloaded: %s', this.friendlyName, this.lastLoaded);
        this.updateHeaders();
    }
    withErrorHandler(value) {
        if (isPromise(value)) {
            return value.catch(error => this.handleError(error));
        }
        if (isFunction(value)) {
            return new Promise((reject, resolve) => {
                try {
                    resolve(value());
                }
                catch (error) {
                    reject(error);
                }
            }).catch(error => this.handleError(error));
        }
    }
    handleError(error, mode) {
        const handleLocal = mode === 'local' || (this.ux && this.ux.errorHandling === 'local');
        if (handleLocal) {
            this.error = error;
            return;
        }
        console.error(error);
        this.$imodal.error({ error });
    }
    get documentTitle() {
        if (this.document) {
            const result = this.document.title;
            if (isArray(result)) {
                return filter(result, p => isString(p) && !isEmpty(p)).join(' | ');
            }
            return result;
        }
    }
    registerForm(name, form) {
        this.$set(this.forms, name, form);
        if (!isRootComponent(this)) {
            const root = this.getRootPageComponent();
            if (!root) {
                throw new Error(`Root page component not found from "${this.friendlyName}".`);
            }
            root.registerForm(name, form);
            return;
        }
        log('[%s] register form %s %o', this.friendlyName, name, this.forms);
    }
    unregisterForm(form) {
        if (this.forms[form.name]) {
            this.forms[form.name] = null;
            log('[%s] unregister form %s %o', this.friendlyName, name, form);
        }
        if (!isRootComponent(this)) {
            const root = this.getRootPageComponent();
            if (!root) {
                throw new Error(`Root page component not found from "${this.friendlyName}".`);
            }
            root.unregisterForm(form);
            return;
        }
    }
    cancelChange(inputId) {
        return true;
    }
    getParentAppComponent() {
        let parent = this;
        while (true) {
            parent = parent.$parent;
            if (parent === this.$root) {
                break;
            }
            if (isComponentContext(parent)) {
                return parent;
            }
        }
    }
    getRootPageComponent() {
        let parent = this;
        while (true) {
            parent = parent.$parent;
            if (parent === this.$root) {
                break;
            }
            if (isComponentContext(parent) && isRootComponent(parent)) {
                return parent;
            }
        }
    }
};
__decorate([
    Prop(),
    __metadata("design:type", Function)
], UserComponentMixin.prototype, "componentInitErrorCallback", void 0);
__decorate([
    Prop(),
    __metadata("design:type", Function)
], UserComponentMixin.prototype, "componentInitReadyCallback", void 0);
__decorate([
    Prop({ default: false }),
    __metadata("design:type", Boolean)
], UserComponentMixin.prototype, "modal", void 0);
__decorate([
    Prop({ default: null }),
    __metadata("design:type", String)
], UserComponentMixin.prototype, "modalName", void 0);
__decorate([
    Prop(),
    __metadata("design:type", Object)
], UserComponentMixin.prototype, "reload", void 0);
__decorate([
    Watch('reload'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], UserComponentMixin.prototype, "onReloadChange", null);
UserComponentMixin = __decorate([
    Component({
        provide() {
            return { componentContext: this };
        }
    })
], UserComponentMixin);
export { UserComponentMixin };
export class ModalUserComponentMixin extends UserComponentMixin {
    close() {
        this.$imodal.close({});
    }
    cancel() { }
}
function isRootComponent(c) {
    return ['page', 'modal'].indexOf(c.componentMetadata.kind) > -1;
}
function isComponentContext(target) {
    return isObject(get(target, 'componentMetadata'));
}
//# sourceMappingURL=component-mixins.js.map